/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { SupportSessionBanner } from "@/features/support-access/SupportSessionBanner";
import React, { ReactNode, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { AuthenticationProvider } from "@/context/authentication";
import { LoadingProvider } from "@/context/LoadingContext";
import { UIProvider } from "@/shared/ui";
import ReCaptchaProvider from "@/shared/ui/components/providers/ReCaptchaProvider";
import { useRecaptchaWarmup } from "@/shared/utils/use-recaptcha-warmup";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { globalErrorContext } from "@/context/ErrorContext";
import useGaPageViews from "@/shared/hooks/use-ga-page-views";
import { useLogoPreload } from "@/shared/hooks";
import GlobalHeader from "@/shared/components/GlobalHeader";
import { CduConsentBanner } from "@/features/cookie-consent";
import { SEOProvider } from "@/shared/seo";
import { DevCockpitProvider } from "@/features/dev-cockpit";
import { AntiBotErrorOverlay } from "@/shared/ui/components/overlays/AntiBotErrorOverlay";
import { ReferralCaptureProvider } from "@/shared/ui/components/providers/ReferralCaptureProvider";
import AuthGate from "@/layout/gates/AuthGate";
import { EnvironmentBookmark } from "@/shared/components/EnvironmentBookmark";
import { usesSidebarChrome } from "@/layout/agency/agency-shell-chrome";
import { useIsMobile } from "@/shared/hooks/use-is-mobile";

interface MainProviderProps {
  children: ReactNode;
}

/**
 * MainProvider component that wraps the application with all necessary providers.
 *
 * Provides:
 * - QueryClient for React Query
 * - UIProvider with light theme
 * - LoadingProvider for loading state management
 * - ErrorContextProvider for global error handling
 * - AuthenticationProvider for authentication state
 *
 * @param {MainProviderProps} props - Component props
 * @param {ReactNode} props.children - Child components to wrap
 * @returns {JSX.Element} Provider-wrapped application
 */
export const MainProvider: React.FC<MainProviderProps> = ({ children }) => {
  const { Provider: ErrorContextProvider } = globalErrorContext;
  useGaPageViews();
  useLogoPreload();
  useRecaptchaWarmup();

  const cockpitToggleRef = useRef<(() => void) | null>(null);
  const [, forceUpdate] = useState(0);

  /*
   * The public landing page at `/` brings its own sticky header, taller than
   * --header-height and with its own section navigation. It therefore opts out
   * of both the GlobalHeader and the header-height offset the page shell
   * reserves for it — otherwise the route shows two stacked headers and loses
   * 48px of the hero's viewport.
   *
   * The comparison trims trailing slashes instead of testing for `"/"`: the
   * router still matches the landing route for `//`, so an exact test left the
   * page with both headers stacked and the avatar sitting over the logo.
   */
  const pathname = useLocation().pathname;
  const isLandingRoute = pathname.replace(/\/+$/, "") === "";

  /*
   * The desktop agency shell now carries the brand, the feedback trigger and
   * the user menu in its own sidebar, so the global bar above it would be an
   * empty duplicate strip. It opts out of the bar and of the header-height
   * offset alike, handing those 48px back to the workspace.
   */
  const isSidebarChromeRoute = usesSidebarChrome(pathname, useIsMobile());
  const isChromeless = isLandingRoute || isSidebarChromeRoute;

  const handleToggleReady = useRef((fn: () => void) => {
    cockpitToggleRef.current = fn;
    forceUpdate((n) => n + 1);
  }).current;

  // Create QueryClient instance with useMemo to avoid recreating on every render
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
    []
  );

  return (
    <SEOProvider>
      <QueryClientProvider client={queryClient}>
        <UIProvider>
          <LoadingProvider>
            <ErrorContextProvider>
              <ReCaptchaProvider>
                <AuthenticationProvider>
                  <AuthGate>
                    <ReferralCaptureProvider>
                        {!isChromeless && <GlobalHeader />}
                        <div
                          data-testid="page-shell"
                          style={{
                            // The page shell spans the full viewport below the header.
                            // Bottom-nav clearance is NOT reserved globally here — only the
                            // layouts that actually render a bottom nav reserve it locally
                            // (AgencyDashboardLayout via fixed inset + paddingBottom,
                            // UserSettingsLayout via its own padding). Reserving it globally
                            // produced a phantom empty strip on nav-less screens (workspace,
                            // onboarding, agencies selection).
                            height: isChromeless ? 'var(--viewport-height)' : 'var(--shell-height)',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            paddingTop: isLandingRoute ? '0' : 'var(--space-1)',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {children}
                        </div>
                        <CduConsentBanner />
                        <SupportSessionBanner />
                        <EnvironmentBookmark onClick={cockpitToggleRef.current ?? undefined} />
                        <DevCockpitProvider onToggleReady={handleToggleReady} />
                        <AntiBotErrorOverlay />
                    </ReferralCaptureProvider>
                  </AuthGate>
                </AuthenticationProvider>
              </ReCaptchaProvider>
            </ErrorContextProvider>
          </LoadingProvider>
        </UIProvider>
      </QueryClientProvider>
    </SEOProvider>
  );
};
